import React, { useState } from 'react';
import {
  Box,
  Chip,
  Dialog,
  Grow,
  IconButton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import MarkChatReadIcon from '@mui/icons-material/MarkChatRead';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SellIcon from '@mui/icons-material/Sell';
import MapIcon from '@mui/icons-material/Map';
import HubIcon from '@mui/icons-material/Hub';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StarIcon from '@mui/icons-material/Star';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import Settings from './Settings';
import Pipeline from './Pipeline';
import ClientList from './ClientList';
import { isDemoInterest } from '../services/api';
import autoPipelineImg from '../demo/img/connector_auto_pipeline_2.gif';
import customCampaignsImg from '../demo/img/connector_custom_campaigns.png';
import distributionMapImg from '../demo/img/connector-map-grow.gif';
import communicationChannelImg from '../demo/img/connector_communication_channel.png';

const ACCENT = '#ed8936';

type PreviewKey = 'engagement' | 'pipeline' | 'add-client' | 'tag-filter';

interface Feature {
  id: string;
  title: string;
  tagline: string;
  detail: string;
  icon: React.ReactNode;
  /** Renders a twinkling premium star next to the title. */
  premium?: boolean;
  /** Renders an info icon next to the title (same action as the card CTA). */
  info?: boolean;
  cta:
    | { kind: 'preview'; key: PreviewKey; label: string }
    | { kind: 'image'; src: string; label: string; caption: string };
}

const FEATURES: Feature[] = [
  {
    id: 'multi-modal',
    title: 'Multi-Modal',
    tagline: 'WhatsApp · image · voice · file · calendar invite',
    detail: 'Every day of your automated follow-up can mix text, images, voice notes, brochures and even Calendly invites — not just plain messages.',
    icon: <PermMediaIcon />,
    cta: { kind: 'preview', key: 'engagement', label: 'See the 7-Day Engagement live' },
  },
  {
    id: 'pipeline',
    title: 'Sales Pipeline Management',
    tagline: 'Every lead on a board, no one slips through',
    detail: 'Drag clients from New → Contacted → Viewing → Closed. See exactly where every deal sits and what needs your attention today.',
    icon: <ViewKanbanIcon />,
    cta: { kind: 'preview', key: 'pipeline', label: 'Open the Pipeline live' },
  },
  {
    id: 'auto-response',
    title: 'Auto Response Tracking',
    tagline: 'Know the moment a client replies',
    detail: 'Inbound replies are detected automatically — engagement pauses so you never spam a client who has already answered you.',
    icon: <MarkChatReadIcon />,
    info: true,
    cta: { kind: 'image', src: autoPipelineImg, label: 'Quick look', caption: 'Replies are tracked automatically and your sequences react to them — no manual bookkeeping.' },
  },
  {
    id: 'hyper-personalization',
    title: 'HyperPersonalization',
    tagline: 'Capture budget, location, projects — in seconds',
    detail: 'One quick form captures the client’s type, budget, preferred location and the projects you were hyping — so every automated message feels hand-written.',
    icon: <PersonSearchIcon />,
    cta: { kind: 'preview', key: 'add-client', label: 'Try the Quick Add Client' },
  },
  {
    id: 'advanced-customization',
    title: 'Advanced Customization',
    tagline: 'Build your own campaigns, beyond the 7-day flow',
    detail: 'Design multi-step WhatsApp journeys for every segment — buyers, sellers, hyping leads — and enrol the right clients into each one.',
    icon: <AutoFixHighIcon />,
    premium: true,
    cta: { kind: 'image', src: customCampaignsImg, label: 'Quick look', caption: 'Custom Campaigns: your own message sequences, targeted at the right leads, fully automated.' },
  },
  {
    id: 'compartmentalization',
    title: 'Compartmentalization of Leads',
    tagline: 'With EOI Integration',
    detail: 'Tag leads into EOI, Hyping, FB and any bucket you like — then filter, bulk-message and enrol each segment in one click.',
    icon: <SellIcon />,
    cta: { kind: 'preview', key: 'tag-filter', label: 'See tag filtering live' },
  },
  {
    id: 'distribution-map',
    title: 'Client Distribution Map',
    tagline: 'See your farming ground at a glance',
    detail: 'Every client plotted on a live map of Singapore — spot clusters, own your patch and plan your next roadshow where your leads actually are.',
    icon: <MapIcon />,
    premium: true,
    cta: { kind: 'image', src: distributionMapImg, label: 'Quick look', caption: 'Your clients, geo-plotted. Filter by stage, tag or campaign and watch your territory light up.' },
  },
  {
    id: 'multi-channel',
    title: 'Multi Communication Channel',
    tagline: 'WhatsApp today — Email & Official API next',
    detail: 'Send from your own WhatsApp now, with Email and the Official WhatsApp Business API on the roadmap — one sequence, every channel.',
    icon: <HubIcon />,
    cta: { kind: 'image', src: communicationChannelImg, label: 'Quick look', caption: 'One engagement engine, many channels: Agent WhatsApp, Email and the Official WhatsApp Cloud API.' },
  },
];

const PREVIEW_HINTS: Record<PreviewKey, string> = {
  engagement: 'This is the real Campaigns → 7-Day Engagement screen, loaded with sample data. Expand a day to see text, images, voice and calendar steps.',
  pipeline: 'This is the real Pipeline module with sample clients. Drag cards between stages to feel the flow.',
  'add-client': 'The Quick Add Client form just popped out on the right — this is how fast a walk-in becomes a nurtured lead.',
  'tag-filter': 'The Tag filter has opened by itself — tick EOI or Hyping Leads and watch the list compartmentalize instantly.',
};

function PreviewContent({ k }: { k: PreviewKey }) {
  switch (k) {
    case 'engagement':
      return <Settings mode="campaigns" initialTab={1} />;
    case 'pipeline':
      return <Pipeline />;
    case 'add-client':
      return <ClientList spotlight="add-client" />;
    case 'tag-filter':
      return <ClientList spotlight="tag-filter" />;
    default:
      return null;
  }
}

export default function WhySuperConnector() {
  // Stack the preview below (dialog) instead of side-by-side on narrow screens.
  const isNarrow = useMediaQuery('(max-width:1100px)');
  const [preview, setPreview] = useState<Feature | null>(null);
  const [imageFeature, setImageFeature] = useState<Feature | null>(null);

  const activateFeature = (f: Feature) => {
    if (f.cta.kind === 'image') {
      setImageFeature(f);
    } else {
      setPreview(f);
    }
  };

  const previewKey = preview && preview.cta.kind === 'preview' ? preview.cta.key : null;

  const previewHeader = preview && (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 2,
        py: 1.25,
        borderBottom: '1px solid rgba(var(--color-text-rgb), 0.1)',
        bgcolor: 'var(--color-surface)',
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          bgcolor: '#22c55e',
          flexShrink: 0,
          animation: 'whyLiveDot 1.4s ease-in-out infinite',
          '@keyframes whyLiveDot': {
            '0%, 100%': { boxShadow: '0 0 0 0 rgba(34, 197, 94, 0.6)' },
            '50%': { boxShadow: '0 0 0 6px rgba(34, 197, 94, 0)' },
          },
        }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography fontWeight={800} sx={{ fontSize: '0.95rem', lineHeight: 1.2 }} noWrap>
          {preview.title} — live preview
        </Typography>
        <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', display: 'block' }} noWrap>
          {previewKey ? PREVIEW_HINTS[previewKey] : ''}
        </Typography>
      </Box>
      <IconButton size="small" onClick={() => setPreview(null)} aria-label="Close preview">
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  const previewBody = previewKey && (
    // Remount per feature so each demo re-runs its entrance (drawer pop, dropdown open).
    <Box
      key={preview!.id}
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        p: 2,
        bgcolor: 'var(--color-bg)',
        animation: 'whyPreviewIn 0.45s ease both',
        '@keyframes whyPreviewIn': {
          from: { opacity: 0, transform: 'translateX(24px)' },
          to: { opacity: 1, transform: 'translateX(0)' },
        },
      }}
    >
      <PreviewContent k={previewKey} />
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
      {/* ── Hero ── */}
      <Box sx={{ mb: 3, textAlign: { xs: 'center', md: 'left' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, justifyContent: { xs: 'center', md: 'flex-start' }, mb: 0.75 }}>
          <RocketLaunchIcon
            sx={{
              color: ACCENT,
              fontSize: 34,
              animation: 'whyRocketFloat 2.6s ease-in-out infinite',
              '@keyframes whyRocketFloat': {
                '0%, 100%': { transform: 'translateY(0) rotate(0deg)' },
                '50%': { transform: 'translateY(-6px) rotate(6deg)' },
              },
            }}
          />
          <Typography
            variant="h4"
            fontWeight={900}
            sx={{
              background: `linear-gradient(90deg, ${ACCENT}, #f59e0b, #a855f7, #3b82f6, ${ACCENT})`,
              backgroundSize: '300% 100%',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              animation: 'whyHeroShift 6s linear infinite',
              '@keyframes whyHeroShift': {
                from: { backgroundPosition: '0% 50%' },
                to: { backgroundPosition: '300% 50%' },
              },
            }}
          >
            Why SUPER Connector?
          </Typography>
        </Box>
        <Typography sx={{ color: 'var(--color-text-subtle)', maxWidth: 640, mx: { xs: 'auto', md: 0 } }}>
          Your entire follow-up machine — on autopilot. This is a live, interactive tour:
          tap any feature below and try it for real with sample data.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-start' } }}>
          <Chip
            icon={<TouchAppIcon sx={{ fontSize: 16 }} />}
            label="Fully interactive"
            size="small"
            sx={{ bgcolor: `${ACCENT}22`, color: ACCENT, fontWeight: 700 }}
          />
          <Chip
            icon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
            label="Real modules, sample data"
            size="small"
            sx={{ bgcolor: 'rgba(168, 85, 247, 0.15)', color: '#a855f7', fontWeight: 700 }}
          />
        </Box>
      </Box>

      {/* ── Features + live preview ── */}
      <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
        {/* Feature cards */}
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            width: isNarrow ? '100%' : 430,
            flexShrink: 0,
            gridTemplateColumns: isNarrow ? { xs: '1fr', sm: '1fr 1fr' } : '1fr',
          }}
        >
          {FEATURES.map((f, i) => {
            const isActive = preview?.id === f.id;
            return (
              <Box
                key={f.id}
                onClick={() => activateFeature(f)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activateFeature(f); } }}
                sx={{
                  position: 'relative',
                  cursor: 'pointer',
                  borderRadius: 2.5,
                  p: 2,
                  bgcolor: 'var(--color-surface)',
                  border: '1px solid',
                  borderColor: isActive ? ACCENT : 'rgba(var(--color-text-rgb), 0.12)',
                  transition: 'transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease',
                  opacity: 0,
                  animation: 'whyCardIn 0.55s ease forwards',
                  animationDelay: `${i * 80}ms`,
                  '@keyframes whyCardIn': {
                    from: { opacity: 0, transform: 'translateY(18px)' },
                    to: { opacity: 1, transform: 'translateY(0)' },
                  },
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    borderColor: ACCENT,
                    boxShadow: `0 10px 28px rgba(237, 137, 54, 0.28)`,
                  },
                  // The pulsing glow lives on a pseudo-element so it can't
                  // restart (and replay) the card's entrance animation.
                  ...(isActive && {
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      inset: -1,
                      borderRadius: 'inherit',
                      pointerEvents: 'none',
                      animation: 'whyCardActiveGlow 1.6s ease-in-out infinite',
                    },
                    '@keyframes whyCardActiveGlow': {
                      '0%, 100%': { boxShadow: `0 0 0 1px ${ACCENT}, 0 0 18px rgba(237, 137, 54, 0.25)` },
                      '50%': { boxShadow: `0 0 0 2px ${ACCENT}, 0 0 30px rgba(237, 137, 54, 0.5)` },
                    },
                  }),
                }}
              >
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {/* Icon tile */}
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      background: `linear-gradient(135deg, ${ACCENT}, #f5a623)`,
                      boxShadow: '0 4px 12px rgba(237, 137, 54, 0.35)',
                    }}
                  >
                    {f.icon}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      <Typography fontWeight={800} sx={{ fontSize: '0.95rem', lineHeight: 1.25 }}>
                        {f.title}
                      </Typography>
                      {f.premium && (
                        <StarIcon
                          sx={{
                            fontSize: 16,
                            color: '#f5c518',
                            animation: 'whyStarTwinkle 1.6s ease-in-out infinite',
                            '@keyframes whyStarTwinkle': {
                              '0%, 100%': { opacity: 1, transform: 'scale(1) rotate(0deg)' },
                              '50%': { opacity: 0.55, transform: 'scale(1.3) rotate(18deg)' },
                            },
                          }}
                        />
                      )}
                      {f.info && (
                        <InfoOutlinedIcon sx={{ fontSize: 15, color: 'var(--color-text-faint)' }} />
                      )}
                    </Box>
                    <Typography variant="caption" sx={{ display: 'block', color: ACCENT, fontWeight: 700, mb: 0.5 }}>
                      {f.tagline}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', color: 'var(--color-text-subtle)', lineHeight: 1.45 }}>
                      {f.detail}
                    </Typography>
                  </Box>
                </Box>
                {/* CTA */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1.25,
                      py: 0.4,
                      borderRadius: 99,
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      color: isActive ? '#fff' : ACCENT,
                      bgcolor: isActive ? ACCENT : `${ACCENT}1a`,
                      border: `1px solid ${isActive ? ACCENT : `${ACCENT}55`}`,
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {f.cta.label}
                    <ArrowForwardIcon
                      sx={{
                        fontSize: 14,
                        animation: 'whyCtaNudge 1.4s ease-in-out infinite',
                        '@keyframes whyCtaNudge': {
                          '0%, 100%': { transform: 'translateX(0)' },
                          '50%': { transform: 'translateX(3px)' },
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Live preview panel (desktop) */}
        {!isNarrow && (
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              position: 'sticky',
              top: 8,
              height: 'calc(100vh - 64px - 48px)',
              minHeight: 480,
            }}
          >
            {preview && previewKey ? (
              <Box
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  p: '2px',
                  background: `linear-gradient(120deg, ${ACCENT}, #a855f7, #3b82f6, ${ACCENT})`,
                  backgroundSize: '300% 300%',
                  animation: 'whyPanelBorder 6s linear infinite',
                  '@keyframes whyPanelBorder': {
                    from: { backgroundPosition: '0% 50%' },
                    to: { backgroundPosition: '300% 50%' },
                  },
                  boxShadow: '0 16px 48px rgba(0, 0, 0, 0.25)',
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    borderRadius: 2.6,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: 'var(--color-bg)',
                  }}
                >
                  {previewHeader}
                  {previewBody}
                </Box>
              </Box>
            ) : (
              /* Empty-state placeholder */
              <Box
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  border: '2px dashed rgba(var(--color-text-rgb), 0.18)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                  textAlign: 'center',
                  px: 4,
                }}
              >
                <TouchAppIcon
                  sx={{
                    fontSize: 56,
                    color: ACCENT,
                    animation: 'whyPokeLeft 1.6s ease-in-out infinite',
                    '@keyframes whyPokeLeft': {
                      '0%, 100%': { transform: 'translateX(0)' },
                      '50%': { transform: 'translateX(-12px)' },
                    },
                  }}
                />
                <Typography variant="h6" fontWeight={800}>
                  Pick a feature on the left
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)', maxWidth: 380 }}>
                  The real module opens right here — pipelines you can drag, dropdowns that
                  open themselves, and forms that pop out. Go on, click one.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {isDemoInterest() && (
      <Box sx={{ textAlign: 'center', mt: 4, mb: 6 }}>
        <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)', fontWeight: 600 }}>
          Like what you see? Register your interest from the banner below
        </Typography>
        <Typography
          sx={{
            fontSize: '1.4rem',
            lineHeight: 1,
            mt: 0.5,
            animation: 'whyBounceDown 1.4s ease-in-out infinite',
            '@keyframes whyBounceDown': {
              '0%, 100%': { transform: 'translateY(0)' },
              '50%': { transform: 'translateY(6px)' },
            },
          }}
        >
          👇
        </Typography>
      </Box>
      )}

      {/* Live preview as a full-screen dialog on narrow screens */}
      {isNarrow && (
        <Dialog
          fullScreen
          open={!!preview && !!previewKey}
          onClose={() => setPreview(null)}
          TransitionComponent={Grow}
          PaperProps={{ sx: { bgcolor: 'var(--color-bg)', color: 'var(--color-text-strong)', display: 'flex', flexDirection: 'column' } }}
        >
          {previewHeader}
          {previewBody}
        </Dialog>
      )}

      {/* Image quick-look dialog */}
      <Dialog
        open={!!imageFeature}
        onClose={() => setImageFeature(null)}
        maxWidth="md"
        fullWidth
        TransitionComponent={Grow}
        PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)', borderRadius: 3, overflow: 'hidden' } }}
      >
        {imageFeature && imageFeature.cta.kind === 'image' && (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 2.5,
                py: 1.5,
                background: `linear-gradient(120deg, ${ACCENT}, #f5a623)`,
                color: '#fff',
              }}
            >
              <Box sx={{ display: 'inline-flex', color: '#fff' }}>{imageFeature.icon}</Box>
              <Typography fontWeight={800} sx={{ flex: 1, minWidth: 0 }} noWrap>
                {imageFeature.title}
              </Typography>
              {imageFeature.premium && (
                <Chip
                  icon={<StarIcon sx={{ fontSize: 14, color: '#f5c518 !important' }} />}
                  label="Premium"
                  size="small"
                  sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: '#fff', fontWeight: 800 }}
                />
              )}
              <IconButton size="small" onClick={() => setImageFeature(null)} sx={{ color: '#fff' }} aria-label="Close">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            {/* Screenshots have light backgrounds — keep the stage white in dark mode too. */}
            <Box sx={{ bgcolor: '#fff', maxHeight: '70vh', overflowY: 'auto' }}>
              <Box
                component="img"
                src={imageFeature.cta.src}
                alt={imageFeature.title}
                sx={{ width: '100%', display: 'block' }}
              />
            </Box>
            <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutoAwesomeIcon sx={{ color: ACCENT, fontSize: 18 }} />
              <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)' }}>
                {imageFeature.cta.caption}
              </Typography>
            </Box>
          </>
        )}
      </Dialog>
    </Box>
  );
}
